// Sentry is intentionally lazy-loaded so `@sentry/react` (+ Replay, ~430 KB) is NOT
// part of the landing page's initial JS. The browser bundle only pulls Sentry in via
// dynamic import when `VITE_SENTRY_DSN` is configured.
//
// If you set VITE_SENTRY_DSN, initSentry() runs as an async fire-and-forget and the
// heavy Sentry chunk loads lazily on first use.

// @ts-nocheck
type SentryModule = typeof import('@sentry/react')

let sentryPromise: Promise<SentryModule> | null = null

function loadSentry(): Promise<SentryModule> | null {
  const dsn = import.meta.env.VITE_SENTRY_DSN?.trim()
  if (!dsn) return null
  if (!sentryPromise) {
    sentryPromise = import('@sentry/react')
  }
  return sentryPromise
}

const APP_VERSION = __APP_VERSION__ ?? '0.0.0'

export function initSentry(): void {
  void (async () => {
    const mod = await loadSentry()
    const dsn = import.meta.env.VITE_SENTRY_DSN?.trim()
    if (!mod || !dsn) return
    mod.init({
      dsn,
      release: `safepsy-web@${APP_VERSION}`,
      environment: import.meta.env.PROD ? 'production' : 'development',
      sendDefaultPii: false,
      tracesSampleRate: 0.15,
      replaysSessionSampleRate: 0.05,
      replaysOnErrorSampleRate: 1.0,
      integrations: [
        mod.replayIntegration(),
        mod.browserTracingIntegration(),
      ],
      ignoreErrors: [
        // Known non-errors from third-party SDKs / browser
        'ResizeObserver loop limit exceeded',
      ],
    })
  })()
}

export async function captureException(error: unknown, context?: Record<string, string>): Promise<void> {
  const mod = await loadSentry()
  const dsn = import.meta.env.VITE_SENTRY_DSN?.trim()
  if (!mod || !dsn) return

  mod.withScope((scope) => {
    if (context) {
      for (const [k, v] of Object.entries(context)) {
        scope.setTag(k, v)
      }
    }
    mod.captureException(error)
  })
}

export async function captureMessage(
  message: string,
  context?: Record<string, string>,
  level: 'info' | 'warning' | 'error' = 'warning',
): Promise<void> {
  const mod = await loadSentry()
  const dsn = import.meta.env.VITE_SENTRY_DSN?.trim()
  if (!mod || !dsn) return

  mod.withScope((scope) => {
    if (context) {
      for (const [k, v] of Object.entries(context)) {
        scope.setTag(k, v)
      }
    }
    mod.captureMessage(message, { level })
  })
}

export async function addBreadcrumb(category: string, message: string, data?: Record<string, unknown>): Promise<void> {
  const mod = await loadSentry()
  const dsn = import.meta.env.VITE_SENTRY_DSN?.trim()
  if (!mod || !dsn) return

  mod.addBreadcrumb({ category, message, data, level: 'info' })
}
